"use client";

import React, { useEffect, useState, useRef } from "react";
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { useTheme } from 'next-themes';
import { useToast } from '@/components/ui/use-toast';
import { useSession } from '@/context/SessionContext';
import { useSettingsSidebar } from '@/context/SettingsSidebarContext';
import { motion, AnimatePresence } from 'framer-motion';
import { getProfileAppearance, updateProfileAppearance } from '@/lib/supabase';
import { Button } from "@/components/ui/button";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Slider } from '@/components/ui/slider';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { X, Settings, Paintbrush, Sun, Moon, SunMoon, Tag, CheckCircle, Clock, Volume2, VolumeX, ArrowRight, LogIn, RotateCw } from '@/components/icons';
import { cn } from '@/lib/utils';
import { resolveDefaultPrimaryHex } from '@/lib/appearanceDefaults';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getAllCategories, Category as CategoryType } from '@/lib/supabase';
import { useSoundEnabled } from '@/context/SoundContext';
import { Switch } from '@/components/ui/switch';

type Appearance = {
  palette: string;
  customPrimary?: string;
  customAccent?: string;
  customForeground?: string;
  theme?: 'light' | 'dark' | 'auto';
  fontScale?: 'small' | 'normal' | 'large';
  fontScaleLevel?: 1 | 2 | 3 | 4;
};

const FONT_SIZE_PX_VALUES: Record<1 | 2 | 3 | 4, string> = {
  1: '14px',
  2: '15px',
  3: '16px',
  4: '17px',
};

function fontScalePxLabel(level: 1 | 2 | 3 | 4): string {
  return FONT_SIZE_PX_VALUES[level];
}

function normalizeFontScaleLevel(value: unknown): 1 | 2 | 3 | 4 {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 4) {
    return value as 1 | 2 | 3 | 4;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed >= 1 && parsed <= 4) {
      return parsed as 1 | 2 | 3 | 4;
    }
  }
  return 3;
}

function fontScaleLabel(level: 1 | 2 | 3 | 4): string {
  if (level === 1) return 'Texto más pequeño';
  if (level === 4) return 'Texto más grande';
  if (level === 3) return 'Tamaño normal';
  return 'Tamaño intermedio';
}

const UPDATE_SLIDER_MIN_WIDTH = 120;
const UPDATE_SLIDER_MAX_WIDTH = 180;
const UPDATE_SLIDER_WIDTH_RATIO = 0.48;
const UPDATE_SLIDER_HANDLE_WIDTH_CSS = 'clamp(120px, 48%, 180px)';
const UPDATE_SLIDER_TRACK_INSET = 1;
const UPDATE_SLIDER_ACTIVATE_RATIO = 0.9;
const UPDATE_SLIDER_IDLE_ARROW_MS = 5600;
const UPDATE_SLIDER_IDLE_REFRESH_MS = 1800;
const UPDATE_SLIDER_IDLE_ICON_FADE_MS = 420;
const UPDATE_SLIDER_ARROW_BOUNCE_MS = 1400;
const UPDATE_SLIDER_ARROW_BOUNCE_X = 1.8;
const UPDATE_SLIDER_COPY_DURATION_CLASS = 'duration-[320ms]';
const UPDATE_SLIDER_EASE_CLASS = '[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]';
const UPDATE_SLIDER_HANDLE_SNAP_MS = 420;
const UPDATE_SLIDER_HANDLE_SNAP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const UPDATE_SLIDER_FILL_SNAP_MS = 180;
const UPDATE_SLIDER_FILL_SNAP_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const PROPRIETARY_COLORS = [
  { id: 'andes', name: 'Andes', hex: '#8B7355' },
  { id: 'dulce-de-leche', name: 'Dulce de Leche', hex: '#f97316' },
  { id: 'estrella-de-mar', name: 'Estrella de Mar', hex: '#fb7185' },
  { id: 'iguazu', name: 'Iguazú', hex: '#10b981' },
  { id: 'jacaranda', name: 'Jacarandá', hex: '#a259ff' },
  { id: 'malbec', name: 'Malbec', hex: '#ef4444' },
  { id: 'mar-del-plata', name: 'Mar del Plata', hex: '#0ea5e9' },
  { id: 'obelisco', name: 'Obelisco', hex: '#334155' },
  { id: 'pampa', name: 'Pampa', hex: '#16a34a' },
  { id: 'patagonia', name: 'Patagonia', hex: '#475569' },
  { id: 'puerto', name: 'Puerto', hex: '#2563eb' },
  { id: 'rio-parana', name: 'Río Paraná', hex: '#06b6d4' },
  { id: 'sierra', name: 'Sierra', hex: '#0ea5b7' },
  { id: 'sol-de-mayo', name: 'Sol de Mayo', hex: '#f59e0b' },
];

function hexToHslString(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyAppearance(settings: Appearance) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const fontScaleLevel = normalizeFontScaleLevel(settings.fontScaleLevel);
  
  if (settings.customPrimary) {
    try {
      const primary = hexToHslString(settings.customPrimary);
      root.style.setProperty('--primary', primary);
    } catch (e) {}
  }
  if (settings.customAccent) {
    try {
      const accent = hexToHslString(settings.customAccent);
      root.style.setProperty('--accent', accent);
    } catch (e) {}
  }

  if (settings.customForeground) {
    try {
      const fg = hexToHslString(settings.customForeground);
      root.style.setProperty('--foreground', fg);
    } catch (e) {}
  } else {
    root.style.removeProperty('--foreground');
  }

  root.style.setProperty('--app-font-size', FONT_SIZE_PX_VALUES[fontScaleLevel]);

  window.dispatchEvent(new CustomEvent('appearance-updated'));
}

/**
 * SettingsSidebar - UN ÚNICO BLOQUE sin tabs
 * Combina Apariencia + Categorías en una sola lista vertical de scroll
 */
export default function SettingsSidebar() {
  const defaultPrimary = resolveDefaultPrimaryHex();
  const { isOpen, close } = useSettingsSidebar();
  const { soundEnabled, setSoundEnabled } = useSoundEnabled();
  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useHeaderOffset();

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      close();
      setIsClosing(false);
    }, 200);
  }, [close]);

  // Appearance state - REINFORCED: CSS default color + auto theme for unauthenticated users
  const [settings, setSettings] = useState<Appearance>({ 
    palette: "custom",
    theme: "auto",
    customPrimary: defaultPrimary,
    fontScaleLevel: 3,
  });
  const settingsRef = useRef<Appearance>({
    palette: "custom",
    theme: "auto",
    customPrimary: defaultPrimary,
    fontScaleLevel: 3,
  });
  const [customPrimary, setCustomPrimary] = useState<string>(defaultPrimary);
  const [customAccent, setCustomAccent] = useState<string>("#c4b5fd");
  const { theme, setTheme } = useTheme();
  const [customForeground, setCustomForeground] = useState<string | undefined>(undefined);
  const { session, profile } = useSession();
  const { toast } = useToast();
  const [checkingAppUpdate, setCheckingAppUpdate] = useState(false);
  const [isRefreshOverlayVisible, setIsRefreshOverlayVisible] = useState(false);
  const [updateSliderX, setUpdateSliderX] = useState(0);
  const [isInteractingWithUpdateSlider, setIsInteractingWithUpdateSlider] = useState(false);
  const [showIdleRefreshIcon, setShowIdleRefreshIcon] = useState(true);
  const wasAnimatingIdleIconsRef = useRef(false);
  const updateSliderTrackRef = useRef<HTMLDivElement>(null);
  const updateSliderHandleRef = useRef<HTMLDivElement>(null);
  const updateSliderDragRef = useRef<{ pointerId: number | null; startClientX: number; startSliderX: number; maxX: number }>({
    pointerId: null,
    startClientX: 0,
    startSliderX: 0,
    maxX: 0,
  });
  const [appearanceLoaded, setAppearanceLoaded] = useState(false);
  const [fontScaleDraftLevel, setFontScaleDraftLevel] = useState<1 | 2 | 3 | 4>(3);
  const [isInteractingWithTypographySlider, setIsInteractingWithTypographySlider] = useState(false);

  const attemptRestoreViewportZoom = React.useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      const active = document.activeElement as HTMLElement | null;
      active?.blur?.();
    } catch (_) {}

    // Reset non-standard CSS zoom if any internal style changed it.
    try { document.documentElement.style.zoom = '1'; } catch (_) {}
    try { document.body.style.zoom = '1'; } catch (_) {}

    // Reassert baseline viewport config (helps in some mobile engines after focus zoom).
    try {
      const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, viewport-fit=cover');
      }
    } catch (_) {}

    window.scrollTo({ top: window.scrollY, left: 0, behavior: 'auto' });
  }, []);

  // Category state
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoggedIn = !!session?.user?.id;
  const profileAvatarUrl = (profile as any)?.avatar_url as string | undefined;
  const profileDisplayName = ((profile as any)?.display_name || (profile as any)?.full_name || '').trim();
  const profileInitial = (profileDisplayName || session?.user?.email || 'M').charAt(0).toUpperCase();

  const handleOpenUserMenu = () => {
    handleClose();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('open-user-menu'));
    }, 200);
  };
  // Only show categories on exact /notas or /tiendas routes
  const isNews = location.pathname === '/notas';
  const isBrowse = location.pathname === '/tiendas';
  const showCategories = isNews || isBrowse;
  const [newsCategories, setNewsCategories] = useState<CategoryType[]>([]);
  const selectedCat = searchParams.get('category') || null;
  const isFollowing = searchParams.get('tab') === 'following';
  const isMine = selectedCat === 'mine';

  const productCategories = [
    { value: 'book', label: 'Libros' },
    { value: 'electronics', label: 'Electrónica' },
    { value: 'vehicle', label: 'Vehículos' },
    { value: 'propiedades', label: 'Propiedades' },
    { value: 'diversion', label: 'Diversión' },
    { value: 'art', label: 'Arte' },
    { value: 'clothing', label: 'Ropa' },
    { value: 'groceries', label: 'Almacén' },
    { value: 'tours', label: 'Excursiones' },
    { value: 'other', label: 'Otros' },
  ];

  // Effects
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {}, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop && !isInteractingWithTypographySlider && !isInteractingWithUpdateSlider
  });

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  const cacheAppearanceSettings = React.useCallback((s: Appearance) => {
    try {
      const key = profile?.id ? 'appearance-settings' : 'guest-appearance-settings';
      localStorage.setItem(key, JSON.stringify(s));
    } catch (storageError) {
      console.warn('[SettingsSidebar] Could not cache to localStorage:', storageError);
    }
  }, [profile?.id]);

  // Load appearance settings
  useEffect(() => {
    const load = async () => {
      try {
        if (profile?.id) {
          const server = await getProfileAppearance(profile.id);
          if (server) {
            setSettings(server);
            if ((server as any).customPrimary) setCustomPrimary((server as any).customPrimary);
            if ((server as any).customAccent) setCustomAccent((server as any).customAccent);
            if ((server as any).customForeground) setCustomForeground((server as any).customForeground);
            if ((server as any).theme) {
              setTheme((server as any).theme);
            }
            cacheAppearanceSettings(server as Appearance);
            return;
          }
        }

        const fallbackRaw =
          localStorage.getItem(profile?.id ? 'appearance-settings' : 'guest-appearance-settings') ||
          localStorage.getItem('appearance-settings') ||
          localStorage.getItem('guest-appearance-settings');

        if (fallbackRaw) {
          const parsed = JSON.parse(fallbackRaw) as Appearance;
          setSettings(parsed);
          if ((parsed as any).customPrimary) setCustomPrimary((parsed as any).customPrimary);
          if ((parsed as any).customAccent) setCustomAccent((parsed as any).customAccent);
          if ((parsed as any).customForeground) setCustomForeground((parsed as any).customForeground);
          if (parsed.theme) {
            setTheme(parsed.theme);
          }
        }
      } catch (e) {
        console.warn('[SettingsSidebar] error loading appearance:', e);
      } finally {
        setAppearanceLoaded(true);
      }
    };
    setAppearanceLoaded(false);
    load();
  }, [profile?.id, setTheme, cacheAppearanceSettings]);

  useEffect(() => {
    if (!appearanceLoaded) return;
    if (typeof window !== "undefined") {
      applyAppearance(settings);
    }
  }, [settings, appearanceLoaded]);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    setFontScaleDraftLevel(normalizeFontScaleLevel(settings.fontScaleLevel));
  }, [settings.fontScaleLevel]);

  useEffect(() => {
    if (!isInteractingWithTypographySlider) return;

    const releaseInteraction = () => setIsInteractingWithTypographySlider(false);
    window.addEventListener('pointerup', releaseInteraction);
    window.addEventListener('pointercancel', releaseInteraction);
    window.addEventListener('touchend', releaseInteraction);
    window.addEventListener('touchcancel', releaseInteraction);

    return () => {
      window.removeEventListener('pointerup', releaseInteraction);
      window.removeEventListener('pointercancel', releaseInteraction);
      window.removeEventListener('touchend', releaseInteraction);
      window.removeEventListener('touchcancel', releaseInteraction);
    };
  }, [isInteractingWithTypographySlider]);

  useEffect(() => {
    if (isOpen && isNews) {
      getAllCategories().then(setNewsCategories).catch(() => {});
    }
  }, [isOpen, isNews]);

  const saveSettings = async (s: Appearance) => {
    const normalizedFontScaleLevel = normalizeFontScaleLevel(s.fontScaleLevel);
    const payload: Appearance = {
      ...s,
      customPrimary: s.customPrimary,
      customAccent: s.customAccent,
      customForeground: s.customForeground,
      theme: s.theme ?? undefined,
      fontScaleLevel: normalizedFontScaleLevel,
    };

    try {
      if (profile?.id) {
        return await updateProfileAppearance(profile.id, payload);
      }
    } catch (err) {
      return null;
    }
    return null;
  };

  const playRefreshOverlayTransition = React.useCallback(async () => {
    setIsRefreshOverlayVisible(true);
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 220);
    });
  }, []);

  const handleCheckLatestVersion = React.useCallback(async () => {
    if (checkingAppUpdate) return false;

    if (typeof window === 'undefined') return false;

    setCheckingAppUpdate(true);

    try {
      const performHardRefresh = async () => {
        try {
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
          }
        } catch (error) {
          console.warn('[SettingsSidebar] Could not clear Cache Storage before refresh:', error);
        }

        const url = new URL(window.location.href);
        url.searchParams.set('swv', Date.now().toString());

        await playRefreshOverlayTransition();
        window.location.replace(url.toString());
      };

      let hasNewVersion = false;

      if ('serviceWorker' in navigator) {
        const registration =
          await navigator.serviceWorker.getRegistration('/sw.js') ||
          await navigator.serviceWorker.getRegistration();

        if (registration) {
          hasNewVersion = Boolean(registration.waiting && navigator.serviceWorker.controller);

          if (!hasNewVersion) {
            await registration.update();
            hasNewVersion = Boolean(registration.waiting && navigator.serviceWorker.controller);
          }

          if (!hasNewVersion && registration.installing) {
            hasNewVersion = await new Promise<boolean>((resolve) => {
              const worker = registration.installing;
              if (!worker) {
                resolve(false);
                return;
              }

              let done = false;
              const finish = (value: boolean) => {
                if (done) return;
                done = true;
                resolve(value);
              };

              const timeoutId = window.setTimeout(() => finish(false), 3000);

              const handleStateChange = () => {
                if (worker.state === 'installed') {
                  window.clearTimeout(timeoutId);
                  finish(Boolean(navigator.serviceWorker.controller));
                  return;
                }

                if (worker.state === 'redundant') {
                  window.clearTimeout(timeoutId);
                  finish(false);
                }
              };

              worker.addEventListener('statechange', handleStateChange);
            });
          }

          if (hasNewVersion) {
            sessionStorage.setItem('sw-update-shown', 'true');
            sessionStorage.setItem('sw-update-in-progress', 'true');

            try {
              await registration.update();
            } catch {
              // Continue with current registration state.
            }

            const waitForControllerChange = new Promise<void>((resolve) => {
              let settled = false;
              const done = () => {
                if (settled) return;
                settled = true;
                resolve();
              };

              navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
              window.setTimeout(done, 2500);
            });

            const workerToSkip = registration.waiting || registration.installing;
            workerToSkip?.postMessage({ type: 'SKIP_WAITING' });

            window.dispatchEvent(new CustomEvent('sw-force-update'));

            await waitForControllerChange;
          }
        }
      }

      if (hasNewVersion) {
        toast({
          title: 'Actualizando Minibid',
        });
      }

      await performHardRefresh();
      return true;
    } catch (error) {
      console.warn('[SettingsSidebar] Error checking latest app version:', error);
      setIsRefreshOverlayVisible(false);
      toast({
        title: 'No se pudo verificar la actualización',
        description: 'Intentá nuevamente en unos segundos.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setCheckingAppUpdate(false);
    }
  }, [checkingAppUpdate, playRefreshOverlayTransition, toast]);

  const getUpdateSliderMaxX = React.useCallback(() => {
    const track = updateSliderTrackRef.current;
    if (!track) return 0;
    const handleWidth = updateSliderHandleRef.current?.clientWidth ?? Math.min(
      Math.max(Math.round(track.clientWidth * UPDATE_SLIDER_WIDTH_RATIO), UPDATE_SLIDER_MIN_WIDTH),
      UPDATE_SLIDER_MAX_WIDTH
    );
    const max = track.clientWidth - handleWidth - UPDATE_SLIDER_TRACK_INSET * 2;
    return Math.max(max, 0);
  }, []);

  const resetUpdateSlider = React.useCallback(() => {
    setUpdateSliderX(0);
    setIsInteractingWithUpdateSlider(false);
    updateSliderDragRef.current = {
      pointerId: null,
      startClientX: 0,
      startSliderX: 0,
      maxX: 0,
    };
  }, []);

  const handleUpdateSliderPointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (checkingAppUpdate) return;

    event.stopPropagation();
    event.preventDefault();

    const maxX = getUpdateSliderMaxX();
    updateSliderDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startSliderX: updateSliderX,
      maxX,
    };

    setIsInteractingWithUpdateSlider(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [checkingAppUpdate, getUpdateSliderMaxX, updateSliderX]);

  const handleUpdateSliderPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = updateSliderDragRef.current;
    if (drag.pointerId !== event.pointerId) return;

    event.stopPropagation();
    const delta = event.clientX - drag.startClientX;
    const nextX = Math.min(Math.max(drag.startSliderX + delta, 0), drag.maxX);
    setUpdateSliderX(nextX);
  }, []);

  const finishUpdateSliderDrag = React.useCallback(async (pointerId: number | null) => {
    const drag = updateSliderDragRef.current;
    if (drag.pointerId === null || (pointerId !== null && pointerId !== drag.pointerId)) {
      return;
    }

    const maxX = drag.maxX || getUpdateSliderMaxX();
    const shouldActivate = maxX > 0 && updateSliderX >= maxX * UPDATE_SLIDER_ACTIVATE_RATIO;

    updateSliderDragRef.current.pointerId = null;
    setIsInteractingWithUpdateSlider(false);

    if (!shouldActivate) {
      setUpdateSliderX(0);
      return;
    }

    setUpdateSliderX(maxX);
    const didTriggerRefresh = await handleCheckLatestVersion();
    if (!didTriggerRefresh) {
      setUpdateSliderX(0);
    }
  }, [getUpdateSliderMaxX, handleCheckLatestVersion, updateSliderX]);

  const handleUpdateSliderPointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    void finishUpdateSliderDrag(event.pointerId);
  }, [finishUpdateSliderDrag]);

  const handleUpdateSliderPointerCancel = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    void finishUpdateSliderDrag(event.pointerId);
  }, [finishUpdateSliderDrag]);

  useEffect(() => {
    if (!isOpen) {
      resetUpdateSlider();
      setIsRefreshOverlayVisible(false);
    }
  }, [isOpen, resetUpdateSlider]);

  useEffect(() => {
    const shouldAnimateIdleIcons =
      isOpen &&
      !isDesktop &&
      !checkingAppUpdate &&
      !isInteractingWithUpdateSlider &&
      updateSliderX === 0;

    if (!shouldAnimateIdleIcons) {
      wasAnimatingIdleIconsRef.current = false;
      setShowIdleRefreshIcon(true);
      return;
    }

    const isEnteringIdle = !wasAnimatingIdleIconsRef.current;
    wasAnimatingIdleIconsRef.current = true;

    // On idle entry, force refresh first and start timing on the next pass.
    if (isEnteringIdle && !showIdleRefreshIcon) {
      setShowIdleRefreshIcon(true);
      return;
    }

    const nextSwitchMs = showIdleRefreshIcon ? UPDATE_SLIDER_IDLE_REFRESH_MS : UPDATE_SLIDER_IDLE_ARROW_MS;
    const timeoutId = window.setTimeout(() => {
      setShowIdleRefreshIcon((prev) => !prev);
    }, nextSwitchMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [checkingAppUpdate, isDesktop, isInteractingWithUpdateSlider, isOpen, showIdleRefreshIcon, updateSliderX]);

  // Category handlers
  const handleSelection = (id: string | null) => {
    if (!showCategories) return;
    const newParams = new URLSearchParams(searchParams);
    if (id) {
      newParams.set('category', id);
    } else {
      newParams.delete('category');
    }
    newParams.delete('tab');
    
    const targetPath = isNews ? '/notas' : '/tiendas';
    if (location.pathname !== targetPath) {
      navigate(`${targetPath}?${newParams.toString()}`);
    } else {
      setSearchParams(newParams);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFollowing = () => {
    if (!showCategories) return;
    const newParams = new URLSearchParams();
    newParams.set('tab', 'following');
    
    if (location.pathname === '/notas') {
      setSearchParams(newParams);
    } else {
      navigate(`/notas?${newParams.toString()}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAll = () => {
    if (!showCategories) return;
    const newParams = new URLSearchParams();
    const targetPath = isNews ? '/notas' : '/tiendas';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    } else {
      setSearchParams(newParams);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateSliderMaxX = getUpdateSliderMaxX();
  const isUpdateSliderReady =
    !checkingAppUpdate &&
    updateSliderMaxX > 0 &&
    updateSliderX >= updateSliderMaxX * UPDATE_SLIDER_ACTIVATE_RATIO;
  const isUpdateSliderIdle =
    !checkingAppUpdate &&
    !isInteractingWithUpdateSlider &&
    updateSliderX === 0;
  const shouldHideUpdateSliderCopy = checkingAppUpdate || isInteractingWithUpdateSlider || updateSliderX > 0;
  const shouldAnimateUpdateSliderArrowBounce = !isInteractingWithUpdateSlider;

  return (
    <AnimatePresence>
      {(isOpen && !isClosing) && (
        <>
          <AnimatePresence>
            {isRefreshOverlayVisible && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                className="fixed inset-0 z-[10001] bg-background pointer-events-none"
              />
            )}
          </AnimatePresence>

          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9998]"
            onClick={handleClose}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[9999] flex flex-col'
            )}
            style={{
              top: '0',
              height: '100dvh',
              pointerEvents: 'auto'
            }}
          >
            {/* Safe Area Spacer for Mobile */}
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClose(); }}
                      className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Cerrar</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <ScrollArea ref={scrollAreaRef} className="flex-1">
              <div className="p-4 space-y-8">
                {!isDesktop ? (
                  <div
                    ref={updateSliderTrackRef}
                    className={cn(
                      'relative h-12 w-full rounded-full border border-primary/10 overflow-hidden',
                      checkingAppUpdate ? 'bg-primary/10' : 'bg-card/60'
                    )}
                  >
                      <div
                        className={cn(
                          'absolute rounded-full bg-primary'
                        )}
                        style={{
                          left: `${UPDATE_SLIDER_TRACK_INSET}px`,
                          top: `${UPDATE_SLIDER_TRACK_INSET}px`,
                          bottom: `${UPDATE_SLIDER_TRACK_INSET}px`,
                          width: `calc(${updateSliderX}px + ${UPDATE_SLIDER_HANDLE_WIDTH_CSS})`,
                          transition: isInteractingWithUpdateSlider
                            ? 'none'
                            : `width ${UPDATE_SLIDER_FILL_SNAP_MS}ms ${UPDATE_SLIDER_FILL_SNAP_EASING}`,
                        }}
                      />

                      <p
                        className={cn(
                          'pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-end pr-4 text-right text-[11px] font-semibold tracking-wide text-muted-foreground transition-opacity',
                          UPDATE_SLIDER_COPY_DURATION_CLASS,
                          UPDATE_SLIDER_EASE_CLASS,
                          shouldHideUpdateSliderCopy && 'opacity-0'
                        )}
                      >
                        Deslizá para actualizar
                      </p>

                      <div
                        role="button"
                        aria-label="Deslizar para actualizar"
                        aria-disabled={checkingAppUpdate}
                        tabIndex={-1}
                        onPointerDown={handleUpdateSliderPointerDown}
                        onPointerMove={handleUpdateSliderPointerMove}
                        onPointerUp={handleUpdateSliderPointerUp}
                        onPointerCancel={handleUpdateSliderPointerCancel}
                        className={cn(
                          'absolute z-10 h-11 rounded-full border shadow-sm overflow-hidden',
                          'grid place-items-center touch-none select-none transition-transform',
                          UPDATE_SLIDER_EASE_CLASS,
                          checkingAppUpdate
                            ? 'border-primary/40 bg-primary cursor-wait'
                            : 'border-primary/30 bg-primary active:scale-[0.985]'
                        )}
                        ref={updateSliderHandleRef}
                        style={{
                          width: UPDATE_SLIDER_HANDLE_WIDTH_CSS,
                          left: `${UPDATE_SLIDER_TRACK_INSET}px`,
                          top: `${UPDATE_SLIDER_TRACK_INSET}px`,
                          transform: `translateX(${updateSliderX}px)`,
                          transition: isInteractingWithUpdateSlider
                            ? 'none'
                            : `transform ${UPDATE_SLIDER_HANDLE_SNAP_MS}ms ${UPDATE_SLIDER_HANDLE_SNAP_EASING}`,
                        }}
                      >
                        <span className="relative block h-4 w-4 overflow-hidden">
                          {checkingAppUpdate ? (
                            <span className="absolute inset-0 grid place-items-center">
                              <RotateCw className="h-4 w-4 text-primary-foreground animate-spin [animation-duration:1.8s]" />
                            </span>
                          ) : isUpdateSliderReady ? (
                            <span className="absolute inset-0 grid place-items-center">
                              <RotateCw className="h-4 w-4 text-primary-foreground" />
                            </span>
                          ) : isUpdateSliderIdle ? (
                            <AnimatePresence mode="wait" initial={false}>
                              {showIdleRefreshIcon ? (
                                <motion.span
                                  key="idle-refresh"
                                  className="absolute inset-0 grid place-items-center"
                                  initial={{ opacity: 0, x: 0 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 0 }}
                                  transition={{ duration: UPDATE_SLIDER_IDLE_ICON_FADE_MS / 1000, ease: 'easeInOut' }}
                                >
                                  <RotateCw className="h-4 w-4 text-primary-foreground" />
                                </motion.span>
                              ) : (
                                <motion.span
                                  key="idle-arrow"
                                  className="absolute inset-0 grid place-items-center"
                                  initial={{ opacity: 0, scale: 0.98 }}
                                  animate={{ opacity: 1, scale: 1, x: 0 }}
                                  exit={{ opacity: 0, scale: 0.98, x: 0 }}
                                  transition={{
                                    opacity: { duration: UPDATE_SLIDER_IDLE_ICON_FADE_MS / 1000, ease: 'easeInOut' },
                                    scale: { duration: UPDATE_SLIDER_IDLE_ICON_FADE_MS / 1000, ease: 'easeInOut' },
                                    x: { duration: 0.12, ease: 'easeOut' },
                                  }}
                                >
                                  <motion.span
                                    className="grid place-items-center"
                                    animate={{ x: shouldAnimateUpdateSliderArrowBounce ? [0, UPDATE_SLIDER_ARROW_BOUNCE_X, 0] : 0 }}
                                    transition={
                                      shouldAnimateUpdateSliderArrowBounce
                                        ? { duration: UPDATE_SLIDER_ARROW_BOUNCE_MS / 1000, repeat: Infinity, ease: 'easeInOut' }
                                        : { duration: 0.12, ease: 'easeOut' }
                                    }
                                  >
                                    <ArrowRight className="h-4 w-4 text-primary-foreground" />
                                  </motion.span>
                                </motion.span>
                              )}
                            </AnimatePresence>
                          ) : (
                            <motion.span
                              className="absolute inset-0 grid place-items-center"
                              animate={{ x: shouldAnimateUpdateSliderArrowBounce ? [0, UPDATE_SLIDER_ARROW_BOUNCE_X, 0] : 0 }}
                              transition={
                                shouldAnimateUpdateSliderArrowBounce
                                  ? { duration: UPDATE_SLIDER_ARROW_BOUNCE_MS / 1000, repeat: Infinity, ease: 'easeInOut' }
                                  : { duration: 0.12, ease: 'easeOut' }
                              }
                            >
                              <ArrowRight className="h-4 w-4 text-primary-foreground" />
                            </motion.span>
                          )}
                        </span>
                      </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { void handleCheckLatestVersion(); }}
                    disabled={checkingAppUpdate}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-lg border p-4 text-left transition-colors',
                      checkingAppUpdate
                        ? 'border-primary/20 bg-primary/10 cursor-wait'
                        : 'border-primary/10 bg-card/50 hover:bg-card/70 hover:border-primary/30'
                    )}
                  >
                    <RotateCw className={cn('h-4 w-4 text-muted-foreground shrink-0', checkingAppUpdate && 'animate-spin')} />
                    <p className="text-sm font-semibold text-foreground">Actualizar</p>
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleOpenUserMenu}
                  className="w-full flex items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3 text-left transition-all hover:border-primary/40 hover:bg-primary/10"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isLoggedIn ? (
                      <div className="h-9 w-9 shrink-0 rounded-full overflow-hidden border border-primary/20 bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                        {profileAvatarUrl ? (
                          <img src={profileAvatarUrl} alt={profileDisplayName || 'Perfil'} className="h-full w-full object-cover" />
                        ) : (
                          <span>{profileInitial}</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <LogIn className="w-4 h-4" />
                      </div>
                    )}
                    <div className="min-w-0">
                      {isLoggedIn ? (
                        <>
                          <p className="text-sm font-semibold text-foreground truncate">
                            {profileDisplayName || 'Tu cuenta'}
                          </p>
                          <p className="text-xs text-muted-foreground">Gestioná tu perfil y preferencias ingresando aquí.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-foreground">Entrar a Minibid</p>
                          <p className="text-xs text-muted-foreground">Abrí tu cuenta o iniciá sesión desde aquí.</p>
                        </>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0 text-primary" />
                </button>

                {/* CATEGORÍAS SECTION - ONLY ON /notas AND /tiendas */}
                {showCategories && (
                <div className="space-y-6">
                  <h3 className="text-base font-bold text-foreground flex items-center gap-2 uppercase tracking-wide">
                    <Tag className="w-4 h-4" />
                    Categorías
                  </h3>

                  {/* Filter List */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.15em]">
                        {isNews ? "Filtrar Temas" : "Categorías"}
                      </p>
                      {(selectedCat || isFollowing) && (
                        <button 
                          onClick={handleAll} 
                          className="text-[10px] text-primary hover:underline font-black uppercase tracking-tighter"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>

                    <div className="grid gap-2">
                      <button
                        onClick={handleAll}
                        className={cn(
                          "flex items-center justify-between p-3 rounded-lg text-sm transition-all border",
                          !selectedCat && !isFollowing 
                           ? "bg-primary/10 border-primary text-primary font-bold shadow-xs" 
                           : "bg-card border-primary/10 text-foreground/70 hover:bg-primary/5 hover:border-primary/30"
                        )}
                      >
                        <span>Todo lo reciente</span>
                        {(!selectedCat && !isFollowing) && <CheckCircle className="w-4 h-4" />}
                      </button>

                      {isNews && isLoggedIn && (
                        <button
                          onClick={handleFollowing}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg text-sm transition-all border",
                            isFollowing 
                              ? "bg-primary/10 border-primary text-primary font-bold shadow-xs" 
                              : "bg-card border-primary/10 text-foreground/70 hover:bg-primary/5 hover:border-primary/30"
                          )}
                        >
                          <span>Seguidos</span>
                          {isFollowing && <CheckCircle className="w-4 h-4" />}
                        </button>
                      )}

                      {!isNews && isBrowse && isLoggedIn && (
                        <button
                          onClick={() => handleSelection('mine')}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg text-sm transition-all border",
                            isMine 
                              ? "bg-primary/10 border-primary text-primary font-bold shadow-xs" 
                              : "bg-card border-primary/10 text-foreground/70 hover:bg-primary/5 hover:border-primary/30"
                          )}
                        >
                          <span>Mis productos</span>
                          {isMine && <CheckCircle className="w-4 h-4" />}
                        </button>
                      )}

                      {isNews ? (
                        newsCategories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => handleSelection(cat.id)}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg text-sm transition-all border",
                              selectedCat === cat.id 
                                ? "bg-primary/10 border-primary text-primary font-bold shadow-xs" 
                                : "bg-card border-primary/5 text-foreground/70 hover:bg-primary/5 hover:border-primary/20"
                            )}
                          >
                            <span className="capitalize">{cat.name}</span>
                            {selectedCat === cat.id && <CheckCircle className="w-4 h-4" />}
                          </button>
                        ))
                      ) : (
                        productCategories.map((cat) => (
                          <button
                            key={cat.value}
                            onClick={() => handleSelection(cat.value)}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg text-sm transition-all border",
                              selectedCat === cat.value 
                                ? "bg-primary/10 border-primary text-primary font-bold shadow-xs" 
                                : "bg-card border-primary/5 text-foreground/70 hover:bg-primary/5 hover:border-primary/20"
                            )}
                          >
                            <span>{cat.label}</span>
                            {selectedCat === cat.value && <CheckCircle className="w-4 h-4" />}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {isBrowse && (
                    <div className="mt-8 p-4 rounded-xl bg-primary/5 border border-primary/10">
                      <p className="text-[10px] font-black text-primary mb-2 flex items-center gap-2 uppercase tracking-widest">
                         <Clock className="w-4 h-4" />
                         Filtros inteligentes
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        Estamos trabajando en filtros por distancia y precio para ayudarte a encontrar lo mejor cerca tuyo.
                      </p>
                    </div>
                  )}
                </div>
                )}

                {/* APARIENCIA SECTION - ALWAYS VISIBLE */}
                <div className={cn("space-y-6", showCategories && "border-t pt-6")}>
                  {/* Theme Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Tema</h3>
                    <div className="flex flex-col gap-4 p-4 rounded-lg border bg-card/50 hover:bg-card/70 transition-colors">
                      <div className="flex items-center gap-3">
                        {theme === 'auto' ? (
                          <SunMoon className="h-4 w-4 text-muted-foreground" />
                        ) : theme === 'dark' ? (
                          <Moon className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Sun className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">
                            {theme === 'dark' 
                              ? 'Tema oscuro' 
                              : theme === 'light' 
                              ? 'Tema claro'
                              : 'Automático'
                            }
                          </span>
                        </div>
                      </div>

                      <ToggleGroup
                        type="single"
                        value={theme || 'light'}
                        onValueChange={(value) => {
                          if (value) {
                            const newTheme = value as 'light' | 'dark' | 'auto';
                            setTheme(newTheme);
                            const newSettings = { ...settingsRef.current, theme: newTheme };
                            settingsRef.current = newSettings;
                            setSettings(newSettings);
                            void saveSettings(newSettings);
                            cacheAppearanceSettings(newSettings);
                          }
                        }}
                        className="w-full [&_button[data-state=on]]:bg-primary [&_button[data-state=on]]:text-primary-foreground hover:[&_button[data-state=on]]:bg-primary/90"
                      >
                        <ToggleGroupItem 
                          value="light" 
                          className="flex-1 gap-2"
                          aria-label="Tema claro"
                        >
                          <Sun className="h-4 w-4" />
                          <span className="text-xs sm:text-sm">Claro</span>
                        </ToggleGroupItem>
                        <ToggleGroupItem 
                          value="auto" 
                          className="flex-1 gap-2"
                          aria-label="Tema automático"
                        >
                          <SunMoon className="h-4 w-4" />
                          <span className="text-xs sm:text-sm">Auto</span>
                        </ToggleGroupItem>
                        <ToggleGroupItem 
                          value="dark" 
                          className="flex-1 gap-2"
                          aria-label="Tema oscuro"
                        >
                          <Moon className="h-4 w-4" />
                          <span className="text-xs sm:text-sm">Oscuro</span>
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  </div>

                  {/* Sound Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Sonidos</h3>
                    <div className="flex items-center justify-between p-4 rounded-lg border bg-card/50 hover:bg-card/70 transition-colors">
                      <div className="flex items-center gap-3">
                        {soundEnabled ? (
                          <Volume2 className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <VolumeX className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">
                            {soundEnabled ? 'Sonidos de sistema activados' : 'Sonidos de sistema desactivados'}
                          </span>
                        </div>
                      </div>
                      <Switch
                        checked={soundEnabled}
                        onCheckedChange={setSoundEnabled}
                        className="ml-2"
                      />
                    </div>
                  </div>

                  {/* End padding */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Paleta de colores</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {PROPRIETARY_COLORS.map((c) => (
                        <button
                          key={c.id}
                          onClick={async () => {
                            setCustomPrimary(c.hex);
                            const newSettings = { ...settingsRef.current, palette: 'custom', customPrimary: c.hex } as Appearance;
                            settingsRef.current = newSettings;
                            setSettings(newSettings);
                            try { 
                              applyAppearance(newSettings);
                              await saveSettings(newSettings);
                              cacheAppearanceSettings(newSettings);
                            } catch (e) {}
                          }}
                          aria-label={c.name}
                          className="flex items-center gap-2 p-2.5 rounded-lg border border-primary/10 hover:border-primary/30 bg-card/50 hover:bg-card transition-all"
                        >
                          <span className="w-4 h-4 rounded border border-primary/20" style={{ background: c.hex }} aria-hidden />
                          <span className="text-xs font-medium">{c.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Typography scale */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Tipografía</h3>
                    <div className="flex flex-col gap-4 p-4 rounded-lg border bg-card/50 hover:bg-card/70 transition-colors">
                      {(() => {
                        const activeScaleLevel = fontScaleDraftLevel;
                        return (
                          <>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-xs font-semibold text-muted-foreground">A-</span>
                              <span className="text-sm font-semibold text-foreground text-center">
                                {fontScaleLabel(activeScaleLevel)} · {fontScalePxLabel(activeScaleLevel)}
                              </span>
                              <span className="text-base font-semibold text-muted-foreground">A+</span>
                            </div>

                            <div className="px-3">
                              <div className="relative">
                                <Slider
                                  min={1}
                                  max={4}
                                  step={1}
                                  value={[activeScaleLevel]}
                                  onPointerDown={() => setIsInteractingWithTypographySlider(true)}
                                  onTouchStart={() => setIsInteractingWithTypographySlider(true)}
                                  onValueChange={(value) => {
                                    const nextLevel = normalizeFontScaleLevel(value[0]);
                                    setFontScaleDraftLevel(nextLevel);
                                  }}
                                  onValueCommit={(value) => {
                                    setIsInteractingWithTypographySlider(false);
                                    const nextLevel = normalizeFontScaleLevel(value[0]);
                                    attemptRestoreViewportZoom();
                                    setFontScaleDraftLevel(nextLevel);
                                    const currentLevel = normalizeFontScaleLevel(settingsRef.current.fontScaleLevel);
                                    if (nextLevel === currentLevel) return;
                                    const newSettings = {
                                      ...settingsRef.current,
                                      fontScaleLevel: nextLevel,
                                    } as Appearance;
                                    settingsRef.current = newSettings;
                                    setSettings(newSettings);
                                    applyAppearance(newSettings);
                                    void saveSettings(newSettings);
                                    cacheAppearanceSettings(newSettings);
                                  }}
                                  aria-label="Selector de tamaño de tipografía"
                                />

                                <div className="relative mt-2 h-4 text-[10px] text-muted-foreground/80">
                                  {([1, 2, 3, 4] as const).map((step) => (
                                    <span
                                      key={step}
                                      className={cn(
                                        'absolute top-0 -translate-x-1/2 whitespace-nowrap',
                                        step === 3 && 'font-semibold text-foreground',
                                      )}
                                      style={{ left: `${((step - 1) / 3) * 100}%` }}
                                    >
                                      {fontScalePxLabel(step)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
